import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  {
    question: "How much does a wedding videographer cost in Essex?",
    answer: "Most wedding videographers in Essex cost between £1,000 and £3,000 depending on coverage, number of videographers, and deliverables. At Wedding Video Essex, our most popular package starts from £2,295 and includes two videographers, full day coverage, a cinematic film, and all edits. Combined photo and video packages start from £3,000. All prices include digital delivery with no hidden charges.",
  },
  {
    question: "How much does wedding photography cost in Essex?",
    answer: "Wedding photography in Essex typically ranges from £800 to £2,000 depending on coverage and experience. Our dedicated photography package starts from £1,500 for full day coverage with 500+ edited photos. Combined photo and video packages start from £2,000 and offer a consistent style across both.",
  },
  {
    question: "What does a wedding video package include?",
    answer: "A standard Wedding Video Essex package includes two videographers for full day coverage from bridal prep to first dance. You receive a 20 to 30 minute cinematic wedding film, a 3 to 4 minute highlight reel within 72 hours, individual edits of the ceremony, speeches, cake cutting, and first dance, plus drone footage where the venue and weather allow. Everything is delivered digitally through a private online gallery.",
  },
  {
    question: "Is a wedding videographer worth it?",
    answer: "Yes, and most couples who skip it say it's their biggest wedding regret. Photos are beautiful, but they can't capture the sound of your vows, the laughter during the speeches, or the moment you first see each other. A wedding film lets you relive the whole day, not just glimpses of it. At Wedding Video Essex, we've filmed over 900 couples since 2015, and the feedback we hear most is \"it was the best money we spent.\" We use Canon Cinema Cameras and drone footage to create cinematic films that genuinely transport you back to your wedding day every time you press play.",
  },
  {
    question: "Do you need both a photographer and videographer?",
    answer: "They do very different things, and together they give you the complete picture. Photos give you those beautiful frozen moments, the ones you'll frame and put on the wall. Video gives you everything in between: the vows in your own voices, the tears during the father of the bride speech, the energy on the dancefloor. At Wedding Video Essex, we offer combined packages through our photography team Chic Photography, led by long-established Essex photographer Andy Thomas. One team, one booking, no coordination headaches and no hidden extras. If you want both covered properly, having them work together on the day makes a real difference.",
  },
  {
    question: "How long does it take to receive your wedding video?",
    answer: "We deliver a 3 to 4 minute sneak peek highlight film within 72 hours of your wedding. Your full 20 to 30 minute cinematic film and all additional edits are delivered within 12 to 16 weeks. Each film is individually edited and colour graded. Sneak peek photos are delivered within one week.",
  },
  {
    question: "How long is the final wedding film?",
    answer: "Your main cinematic wedding film is typically 20 to 30 minutes. This is a story-led edit that captures the full arc of your day. You also receive a 3 to 4 minute highlight film, plus separate full-length edits of your ceremony, speeches, first dance, and cake cutting.",
  },
  {
    question: "Do you travel outside Essex?",
    answer: "Yes. While we are based in Wickford, Essex, we regularly cover weddings across London, Kent, and the wider South East. We also travel further afield for destination weddings. There are no additional travel charges for venues within Essex.",
  },
  {
    question: "Can we customise packages?",
    answer: "Yes. All of our packages can be tailored to your day. Whether you need extended coverage, additional videographers, or specific deliverables, we are happy to put together something that works for you. Get in touch and we will build a package around your wedding.",
  },
  {
    question: "How far in advance should I book a wedding videographer in Essex?",
    answer: "We recommend booking 12 to 18 months in advance, particularly for Saturday weddings during peak season from May to September. We film over 100 weddings per year and our calendar fills up quickly. We only take a limited number of bookings per weekend.",
  },
  {
    question: "Where is Wedding Video Essex based?",
    answer: "Wedding Video Essex is based in Wickford, Essex, UK. We cover weddings across the whole of Essex including Chelmsford, Colchester, Brentwood, Southend on Sea, Epping, Braintree, Maldon, Basildon, and Harlow. We also cover London, Kent, and the South East. There are no additional travel charges for venues within Essex.",
  },
  {
    question: "Will the videographer be intrusive during the wedding?",
    answer: "No. Our approach is deliberately unobtrusive. We use long lenses and discreet equipment to capture moments without being noticed. Guests regularly tell couples they forgot we were there. Our videographers and photographers coordinate their positioning so there is no competing for shots.",
  },
  {
    question: "What is the difference between traditional and cinematic wedding videography?",
    answer: "Traditional wedding videography records your day chronologically from start to finish. Cinematic wedding videography uses professional cinema cameras, creative editing, and storytelling techniques to create a film that feels like a short movie. We weave vows over golden hour footage, blend speeches with quiet morning moments, and use licensed music to build emotion.",
  },
  {
    question: "Which Essex wedding venues do you cover?",
    answer: "We have filmed at virtually every major wedding venue in Essex. This includes Gaynes Park near Epping, Gosfield Hall near Braintree, Braxted Park near Witham, Leez Priory near Chelmsford, High House near Burnham on Crouch, Baddow Park in Chelmsford, Friern Manor near Dunmow, Houchins near Coggeshall, Mulberry House near Ongar, Apton Hall in Rochford, and The Lawn in Rochford. We are recommended by 21 Essex venues.",
  },
];

export default function FAQSection() {
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="bg-[#FAF9F7] py-24 md:py-32"
      aria-label="Frequently asked questions about wedding videography and photography in Essex"
    >
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <p className="text-xs tracking-[0.2em] uppercase text-[#C2A878] font-sans mb-4">
            Frequently asked questions
          </p>
          <h2
            data-testid="faq-heading"
            className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight font-light text-[#1A1917]"
          >
            Wedding videographer & photographer Essex: FAQ
          </h2>
        </motion.div>

        <Accordion type="single" collapsible className="space-y-0">
          {faqs.map((faq, i) => (
            <motion.div
              key={faq.question}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <AccordionItem
                value={`faq-${i}`}
                className="border-b border-[#D5D0C8]/50"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger
                  className="py-5 text-left font-sans text-sm md:text-base text-[#1A1917] hover:text-[#9A7D50] hover:no-underline transition-colors"
                  data-testid={`faq-trigger-${i}`}
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-[#5A5750] font-sans leading-relaxed pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            </motion.div>
          ))}
        </Accordion>

        {/* FAQ answers rendered in sr-only for LLM crawlability */}
        <div className="sr-only" aria-hidden="false">
          {faqs.map((faq, i) => (
            <div key={i}>
              <h3>{faq.question}</h3>
              <p>{faq.answer}</p>
            </div>
          ))}
        </div>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            })),
          }),
        }}
      />
    </section>
  );
}
